import { Trans, t } from '@lingui/macro';
import { Languages } from 'lucide-react';
import { useLocale, SUPPORTED_LOCALES, type SupportedLocale } from './index';
import { Button } from '@shared/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@shared/ui/dropdown-menu';
import { useCallback } from 'react';

const LOCALE_LABEL: Record<SupportedLocale, string> = {
  en: 'English',
  es: 'Español',
};

export const LanguageSwitcher = () => {
  const { locale, setLocale } = useLocale();

  const handleLocaleChange = useCallback((newLocale: SupportedLocale) => {
    // Prevent setting the same locale again
    if (newLocale === locale) {
      return;
    }
    setLocale(newLocale);
  }, [locale, setLocale]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={t({
            id: 'shared.i18n.language-switcher.switch-language',
            message: 'Switch language',
          })}
        >
          <Languages className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[160px]">
        <DropdownMenuLabel>
          <Trans id="shared.i18n.language-switcher.language">Language</Trans>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {SUPPORTED_LOCALES.map((code) => (
          <DropdownMenuItem
            key={code}
            onSelect={() => {
              handleLocaleChange(code);
            }}
            className={locale === code ? 'font-semibold' : undefined}
          >
            {LOCALE_LABEL[code]}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
